"use client";

import { c as _c } from "react/compiler-runtime";
import { BUYER_DASHBOARD_QUERY, BUYER_DASHBOARD_SUGGESTION_LIST_QUERY, BUYER_OFFERS_QUERY } from "@/gql/buyer-dashboard";
import { SELLER_UNITS_AND_PROJECTS_QUERY } from "@/gql/sup";
import { GET_UNREAD_COUNTS_FOR_BUYER_GROUP } from "@/lib/graphql/dialog-queries";
import { CategorizedProperties, DashboardCounts, DashboardTab, PropertyDetails, SuggestedProperty } from "@/types/buyer-dashboard";
import { BuyerProject } from "@/types/buyerProject";
import { BuyerUnit } from "@/types/buyerUnit";
import { categorizeItem, createEmptyCategories, createLookupMaps, enhanceItem, extractActiveIds } from "@/utils/buyer-dashboard";
import { logger } from "@/utils/logger";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "./use-auth";
import { useRelationship } from "./use-relationship";
import { useShortlistedProperties } from "./use-shortlisted-properties";
interface UseBuyerDashboardOptions {
  activeTab?: DashboardTab;
}
export function useBuyerDashboard(t0) {
  const $ = _c(103);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    activeTab: t2
  } = t1;
  const activeTab = t2 === undefined ? "suggested" : t2;
  const {
    fetchFreshUserData
  } = useAuth();
  const {
    user,
    buyerGroupId,
    isSeller,
    isAuthenticated
  } = useRelationship();
  const [isRefreshingUserData, setIsRefreshingUserData] = useState(false);
  const shouldSkip = !isAuthenticated || !buyerGroupId || isSeller;
  let t3;
  if ($[2] !== buyerGroupId) {
    t3 = {
      params: {
        groupOne: {
          id: buyerGroupId
        }
      }
    };
    $[2] = buyerGroupId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== shouldSkip || $[5] !== t3) {
    t4 = {
      variables: t3,
      skip: shouldSkip,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all"
    };
    $[4] = shouldSkip;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: buyerData,
    loading: buyerLoading,
    error: buyerError,
    refetch
  } = useQuery(BUYER_DASHBOARD_QUERY, t4);
  let t5;
  if ($[7] !== buyerGroupId) {
    t5 = {
      params: {
        getSuggestedProperties: {
          buyerId: buyerGroupId,
          limit: 100,
          skip: 0,
          status: "pending"
        }
      }
    };
    $[7] = buyerGroupId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== shouldSkip || $[10] !== t5) {
    t6 = {
      variables: t5,
      skip: shouldSkip,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all"
    };
    $[9] = shouldSkip;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const {
    data: suggestedData,
    loading: suggestedLoading,
    error: suggestedError
  } = useQuery(BUYER_DASHBOARD_SUGGESTION_LIST_QUERY, t6);
  let t7;
  if ($[12] !== buyerGroupId) {
    t7 = {
      params: {
        getBuyerOffers: {
          buyerGroupId
        }
      }
    };
    $[12] = buyerGroupId;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== shouldSkip || $[15] !== t7) {
    t8 = {
      variables: t7,
      skip: shouldSkip,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all"
    };
    $[14] = shouldSkip;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const {
    data: offersData
  } = useQuery(BUYER_OFFERS_QUERY, t8);
  let t9;
  if ($[17] !== buyerGroupId) {
    t9 = {
      buyerGroupId
    };
    $[17] = buyerGroupId;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== shouldSkip || $[20] !== t9) {
    t10 = {
      variables: t9,
      skip: shouldSkip,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all"
    };
    $[19] = shouldSkip;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const {
    data: unreadCountsData
  } = useQuery(GET_UNREAD_COUNTS_FOR_BUYER_GROUP, t10);
  useShortlistedProperties(buyerGroupId);
  let t11;
  if ($[22] !== buyerError?.graphQLErrors || $[23] !== buyerGroupId || $[24] !== fetchFreshUserData || $[25] !== isRefreshingUserData) {
    t11 = () => {
      const isGroupNotFoundError = buyerError?.graphQLErrors?.some(_temp);
      if (isGroupNotFoundError && !isRefreshingUserData) {
        setIsRefreshingUserData(true);
        logger.warn("Group not found error detected, refreshing user data...", {
          buyerGroupId
        });
        fetchFreshUserData().finally(() => setIsRefreshingUserData(false));
      }
    };
    $[22] = buyerError?.graphQLErrors;
    $[23] = buyerGroupId;
    $[24] = fetchFreshUserData;
    $[25] = isRefreshingUserData;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== buyerError || $[28] !== buyerGroupId || $[29] !== fetchFreshUserData || $[30] !== isRefreshingUserData) {
    t12 = [buyerError, buyerGroupId, fetchFreshUserData, isRefreshingUserData];
    $[27] = buyerError;
    $[28] = buyerGroupId;
    $[29] = fetchFreshUserData;
    $[30] = isRefreshingUserData;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  useEffect(t11, t12);
  buyerData?.user?.groupOne;
  suggestedData?.buyer?.getSuggestedProperties;
  let t13;
  bb0: {
    const groupOne = buyerData?.user?.groupOne;
    if (!groupOne) {
      let t14;
      let t15;
      if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = createEmptyCategories();
        t15 = [];
        $[32] = t14;
        $[33] = t15;
      } else {
        t14 = $[32];
        t15 = $[33];
      }
      let t16;
      if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = [];
        $[34] = t16;
      } else {
        t16 = $[34];
      }
      let t17;
      if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = {
          categorizedBaseData: t14,
          activeUnitIds: t15 as string[],
          activeProjectIds: t16 as string[]
        };
        $[35] = t17;
      } else {
        t17 = $[35];
      }
      t13 = t17;
      break bb0;
    }
    let categories;
    let t14;
    if ($[36] !== activeTab || $[37] !== groupOne.buyerProjects || $[38] !== groupOne.buyerUnits || $[39] !== suggestedData?.buyer?.getSuggestedProperties) {
      const buyerUnits = groupOne.buyerUnits || [];
      const buyerProjects = groupOne.buyerProjects || [];
      const suggestedProperties = suggestedData?.buyer?.getSuggestedProperties || [];
      categories = createEmptyCategories();
      buyerUnits.forEach(item => categorizeItem(item, categories));
      buyerProjects.forEach(item_0 => categorizeItem(item_0, categories));
      suggestedProperties.forEach(p => categories.suggested.push(p));
      t14 = extractActiveIds(categories, activeTab);
      $[36] = activeTab;
      $[37] = groupOne.buyerProjects;
      $[38] = groupOne.buyerUnits;
      $[39] = suggestedData?.buyer?.getSuggestedProperties;
      $[40] = categories;
      $[41] = t14;
    } else {
      categories = $[40];
      t14 = $[41];
    }
    const {
      unitIds,
      projectIds
    } = t14;
    let t15;
    if ($[42] !== categories || $[43] !== projectIds || $[44] !== unitIds) {
      t15 = {
        categorizedBaseData: categories,
        activeUnitIds: unitIds,
        activeProjectIds: projectIds
      };
      $[42] = categories;
      $[43] = projectIds;
      $[44] = unitIds;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    t13 = t15;
  }
  const {
    categorizedBaseData,
    activeUnitIds,
    activeProjectIds
  } = t13;
  const supQueryVariables = {
    params: {
      details: {
        ids: activeUnitIds,
        projectIds: activeProjectIds,
        type: "sp"
      }
    }
  };
  const {
    data: propertiesData,
    loading: propertiesLoading,
    error: propertiesError
  } = useQuery(SELLER_UNITS_AND_PROJECTS_QUERY, {
    variables: supQueryVariables,
    skip: activeUnitIds.length === 0 && activeProjectIds.length === 0,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all"
  });
  if (propertiesError) {
    logger.graphqlError(propertiesError, "SUP_QUERY", supQueryVariables);
  }
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  if ($[46] !== activeProjectIds || $[47] !== categorizedBaseData.interested || $[48] !== categorizedBaseData.rejected || $[49] !== categorizedBaseData.shortlisted || $[50] !== categorizedBaseData.suggested || $[51] !== categorizedBaseData.visited || $[52] !== offersData?.buyer?.getBuyerOffers || $[53] !== propertiesData?.sellerUnitsAndProjects?.details || $[54] !== unreadCountsData?.dialog?.unreadCountsForBuyerGroup) {
    const propertyDetails = propertiesData?.sellerUnitsAndProjects?.details || [];
    const buyerOffers = offersData?.buyer?.getBuyerOffers || [];
    const unreadCounts = unreadCountsData?.dialog?.unreadCountsForBuyerGroup || [];
    const maps = createLookupMaps(propertyDetails, activeProjectIds, buyerOffers, unreadCounts);
    t14 = categorizedBaseData.shortlisted.map(item_1 => enhanceItem(item_1, maps));
    t15 = categorizedBaseData.interested.map(item_2 => enhanceItem(item_2, maps));
    t16 = categorizedBaseData.visited.map(item_3 => enhanceItem(item_3, maps));
    t17 = categorizedBaseData.rejected.map(item_4 => enhanceItem(item_4, maps));
    t18 = categorizedBaseData.suggested.map(item_5 => enhanceItem(item_5, maps));
    $[46] = activeProjectIds;
    $[47] = categorizedBaseData.interested;
    $[48] = categorizedBaseData.rejected;
    $[49] = categorizedBaseData.shortlisted;
    $[50] = categorizedBaseData.suggested;
    $[51] = categorizedBaseData.visited;
    $[52] = offersData?.buyer?.getBuyerOffers;
    $[53] = propertiesData?.sellerUnitsAndProjects?.details;
    $[54] = unreadCountsData?.dialog?.unreadCountsForBuyerGroup;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
  } else {
    t14 = $[55];
    t15 = $[56];
    t16 = $[57];
    t17 = $[58];
    t18 = $[59];
  }
  let t19;
  if ($[60] !== t14 || $[61] !== t15 || $[62] !== t16 || $[63] !== t17 || $[64] !== t18) {
    t19 = {
      shortlisted: t14,
      interested: t15,
      visited: t16,
      rejected: t17,
      suggested: t18
    };
    $[60] = t14;
    $[61] = t15;
    $[62] = t16;
    $[63] = t17;
    $[64] = t18;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  const categorizedData = t19;
  let t20;
  if ($[66] !== categorizedBaseData) {
    t20 = Object.values(categorizedBaseData).flat();
    $[66] = categorizedBaseData;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== categorizedBaseData.interested.length || $[69] !== categorizedBaseData.rejected.length || $[70] !== categorizedBaseData.shortlisted.length || $[71] !== categorizedBaseData.suggested.length || $[72] !== categorizedBaseData.visited.length || $[73] !== t20.length) {
    t21 = {
      total: t20.length,
      shortlisted: categorizedBaseData.shortlisted.length,
      interested: categorizedBaseData.interested.length,
      visited: categorizedBaseData.visited.length,
      rejected: categorizedBaseData.rejected.length,
      suggested: categorizedBaseData.suggested.length
    };
    $[68] = categorizedBaseData.interested.length;
    $[69] = categorizedBaseData.rejected.length;
    $[70] = categorizedBaseData.shortlisted.length;
    $[71] = categorizedBaseData.suggested.length;
    $[72] = categorizedBaseData.visited.length;
    $[73] = t20.length;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  const counts = t21;
  let t22;
  if ($[75] !== buyerData?.user?.groupOne?.members) {
    t22 = () => buyerData?.user?.groupOne?.members?.find(_temp2);
    $[75] = buyerData?.user?.groupOne?.members;
    $[76] = t22;
  } else {
    t22 = $[76];
  }
  buyerData?.user?.groupOne?.members;
  let t23;
  if ($[77] !== t22) {
    t23 = t22();
    $[77] = t22;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  const decisionMaker = t23;
  buyerData?.user?.groupOne?.processDetails;
  const isAgreementDone = buyerData?.user?.groupOne?.processDetails?.[1]?.status === "completed";
  const t24 = buyerLoading || suggestedLoading;
  const t25 = buyerError || suggestedError;
  const t26 = !!user;
  let t27;
  if ($[79] !== buyerData?.user?.groupOne?.buyerUnits) {
    t27 = buyerData?.user?.groupOne?.buyerUnits || [];
    $[79] = buyerData?.user?.groupOne?.buyerUnits;
    $[80] = t27;
  } else {
    t27 = $[80];
  }
  let t28;
  if ($[81] !== buyerData?.user?.groupOne?.buyerProjects) {
    t28 = buyerData?.user?.groupOne?.buyerProjects || [];
    $[81] = buyerData?.user?.groupOne?.buyerProjects;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  if ($[83] !== isAgreementDone) {
    t29 = {
      isAgreementDone
    };
    $[83] = isAgreementDone;
    $[84] = t29;
  } else {
    t29 = $[84];
  }
  let t30;
  if ($[85] !== decisionMaker) {
    t30 = {
      decisionMaker
    };
    $[85] = decisionMaker;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  if ($[87] !== buyerData || $[88] !== buyerGroupId || $[89] !== categorizedData || $[90] !== counts || $[91] !== isAuthenticated || $[92] !== propertiesData || $[93] !== propertiesLoading || $[94] !== refetch || $[95] !== t24 || $[96] !== t25 || $[97] !== t26 || $[98] !== t27 || $[99] !== t28 || $[100] !== t29 || $[101] !== t30) {
    t31 = {
      data: categorizedData,
      isLoading: t24,
      isEnhancedDataLoading: propertiesLoading,
      error: t25,
      isAuthenticated,
      buyerGroupId,
      hasUserData: t26,
      rawBuyerData: buyerData,
      rawPropertiesData: propertiesData,
      rawBuyerUnits: t27,
      rawBuyerProjects: t28,
      statuses: t29,
      groups: t30,
      counts,
      refetch
    };
    $[87] = buyerData;
    $[88] = buyerGroupId;
    $[89] = categorizedData;
    $[90] = counts;
    $[91] = isAuthenticated;
    $[92] = propertiesData;
    $[93] = propertiesLoading;
    $[94] = refetch;
    $[95] = t24;
    $[96] = t25;
    $[97] = t26;
    $[98] = t27;
    $[99] = t28;
    $[100] = t29;
    $[101] = t30;
    $[102] = t31;
  } else {
    t31 = $[102];
  }
  return t31;
}
function _temp2(member) {
  return member.designation === "Decision Maker";
}
function _temp(err) {
  return err.message?.includes("Group not found");
}